﻿import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  CancelOrderRequest,
  OrderDetailResponse,
  OrderInvoicingResponse,
  OrganizerOrderListQuery,
  OrganizerOrderListResponse,
  RefundOrderRequest,
} from "@ticketnjoy/types";

import { API_BASE_URL, apiClient, problemToApiError } from "@/lib/api-client";
import i18n from "@/lib/i18n";

export const ordersQueryKey = (
  filters: Omit<OrganizerOrderListQuery, "page"> = {},
) => ["orders", "list", filters] as const;

export const orderQueryKey = (orderNumber: string) =>
  ["orders", "detail", orderNumber] as const;

/**
 * Query string builder. Boş/undefined alanlar atlanır ki backend
 * default'larını (page=1, pageSize=25) kullansın.
 */
function toQueryString(query: OrganizerOrderListQuery): string {
  const params = new URLSearchParams();
  if (query.search && query.search.trim()) params.set("search", query.search.trim());
  if (query.status) params.set("status", query.status);
  if (query.page && query.page > 1) params.set("page", String(query.page));
  if (query.pageSize) params.set("pageSize", String(query.pageSize));
  const s = params.toString();
  return s ? `?${s}` : "";
}

/**
 * "Daha fazla yükle" mantığı için infinite query. Her sayfa response'u
 * 25 item döner; frontend tüm sayfaları `flatMap` ile birleştirir.
 * Filtre (search/status) değişince cache key değiştiği için sıfırdan başlar.
 */
export function useOrdersInfiniteQuery(
  filters: Omit<OrganizerOrderListQuery, "page"> = {},
) {
  const pageSize = filters.pageSize ?? 25;
  return useInfiniteQuery<OrganizerOrderListResponse>({
    queryKey: ordersQueryKey(filters),
    queryFn: ({ pageParam = 1 }) =>
      apiClient<OrganizerOrderListResponse>(
        `/orders${toQueryString({ ...filters, page: pageParam as number, pageSize })}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
  });
}

export function useOrderQuery(orderNumber: string | null) {
  return useQuery<OrderDetailResponse>({
    queryKey: orderQueryKey(orderNumber ?? ""),
    queryFn: () => apiClient<OrderDetailResponse>(`/orders/${orderNumber}`),
    enabled: !!orderNumber,
  });
}

/**
 * Cache invalidation: hem detay hem liste sorguları etkilenir.
 * List key'i query-sensitive olduğu için prefix eşleşmesiyle hepsini invalide ederiz.
 */
function useOrderMutationHelpers(orderNumber: string) {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: orderQueryKey(orderNumber) }),
      client.invalidateQueries({ queryKey: ["orders", "list"] }),
    ]);
  };
}

/**
 * Pending sipariş iptali. Reason opsiyonel, boş bırakılabilir.
 */
export function useCancelOrderMutation(orderNumber: string) {
  const invalidate = useOrderMutationHelpers(orderNumber);
  return useMutation<OrderDetailResponse, Error, CancelOrderRequest | void>({
    mutationFn: (body) =>
      apiClient<OrderDetailResponse>(`/orders/${orderNumber}/cancel`, {
        method: "POST",
        body: body ?? {},
      }),
    onSuccess: invalidate,
  });
}

/**
 * Paid (veya PartiallyRefunded) sipariş iadesi. Body:
 * - reason zorunlu (RefundReason enum)
 * - reasonNote: "Other"da zorunlu
 * - ticketIds: null/boş → tam iade; doluysa per-bilet kısmi iade
 * - sendEmail/sendSms: müşteri bildirim toggle (default true)
 *
 * Backend provider'a gerçek iade çağrısı atar; başarısız olursa mutation
 * error state'ine düşer (ApiError.detail kullanıcıya gösterilir). Bu mutation
 * **dashboard query'yi de invalide eder** — refund analizi ve KPI'lar tazelenir.
 */
export function useRefundOrderMutation(orderNumber: string) {
  const client = useQueryClient();
  return useMutation<OrderDetailResponse, Error, RefundOrderRequest>({
    mutationFn: (body) =>
      apiClient<OrderDetailResponse>(`/orders/${orderNumber}/refund`, {
        method: "POST",
        body,
      }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: orderQueryKey(orderNumber) }),
        client.invalidateQueries({ queryKey: ["orders", "list"] }),
        client.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    },
  });
}

/** Onay e-postasını tekrar gönderir (60sn cooldown). */
export function useResendEmailMutation(orderNumber: string) {
  return useMutation<void, Error, void>({
    mutationFn: () =>
      apiClient<void>(`/orders/${orderNumber}/resend-email`, { method: "POST" }),
  });
}

/** Onay SMS'ini tekrar gönderir (60sn cooldown). */
export function useResendSmsMutation(orderNumber: string) {
  return useMutation<void, Error, void>({
    mutationFn: () =>
      apiClient<void>(`/orders/${orderNumber}/resend-sms`, { method: "POST" }),
  });
}

export function useOrderInvoicingQuery(orderNumber: string) {
  return useQuery<OrderInvoicingResponse>({
    queryKey: ["orders", "invoicing", orderNumber] as const,
    queryFn: () =>
      apiClient<OrderInvoicingResponse>(`/orders/${orderNumber}/invoices`),
    enabled: !!orderNumber,
  });
}

/**
 * PDF Paraşüt'ten anlık çekildiği için birkaç saniye sürebilir; fetch + Blob
 * ile indirilir (cross-origin cookie davranışından bağımsız).
 */
export async function downloadInvoicePdf(
  orderNumber: string,
  invoiceId: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/orders/${orderNumber}/invoices/${invoiceId}/pdf`,
    { credentials: "include" },
  );
  if (!response.ok) {
    throw problemToApiError(
      response.status,
      await response.json().catch(() => null),
    );
  }
  const blob = await response.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = i18n.t("orderDetail.invoice.fileName", { orderNumber });
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}
